<script setup>
import { reactive } from "vue";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import Notification from "@/utils/plugins/NotificationService";

const defaultSettings = {
  minimumLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
  sessionDuration: "30",
  rotateRefreshToken: true,
  endSessionsOnPasswordChange: true,
  auditRetention: "365",
  allowAuditExport: true,
  restrictAllowedIps: true,
  allowedIps: "",
};

const settings = reactive({ ...defaultSettings });

const resetSettings = () => {
  Object.assign(settings, defaultSettings);
};

const saveSettings = () => {
  Notification.info(
    "La persistencia de la configuración se habilitará cuando esté disponible el endpoint de Seguridad.",
    4000,
  );
};
</script>

<template>
  <section class="security-configuration">
    <HeaderTitle title="Seguridad" />

    <SecuritySectionTitle title="Configuración" />

    <el-alert class="security-configuration__notice" type="warning" :closable="false" show-icon>
      Estas opciones son gestionadas inicialmente por el backend. Algunos cambios pueden requerir permisos especiales.
    </el-alert>

    <div class="security-configuration__sections">
      <el-card class="security-configuration__card" shadow="never">
        <div class="row">
          <div class="col-12 col-xl-3 security-configuration__intro">
            <h2>Política de contraseñas</h2>
            <p>Establece los requisitos mínimos para las contraseñas de los usuarios.</p>
          </div>
          <div class="col-12 col-xl-9 security-configuration__fields security-configuration__fields--password">
            <div class="form-item">
              <label for="minimum-password-length">Longitud mínima</label>
              <el-input id="minimum-password-length" v-model="settings.minimumLength" class="w-100">
                <template #append>caracteres</template>
              </el-input>
            </div>
            <div class="form-item">
              <label>Requiere mayúsculas</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.requireUppercase }">
                  {{ settings.requireUppercase ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.requireUppercase" />
              </div>
            </div>
            <div class="form-item">
              <label>Requiere minúsculas</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.requireLowercase }">
                  {{ settings.requireLowercase ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.requireLowercase" />
              </div>
            </div>
            <div class="form-item">
              <label>Requiere números</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.requireNumbers }">
                  {{ settings.requireNumbers ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.requireNumbers" />
              </div>
            </div>
            <div class="form-item">
              <label>Requiere símbolos</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.requireSymbols }">
                  {{ settings.requireSymbols ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.requireSymbols" />
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="security-configuration__card" shadow="never">
        <div class="row">
          <div class="col-12 col-xl-3 security-configuration__intro">
            <h2>Sesión y tokens de actualización</h2>
            <p>Configura la duración de sesión y el comportamiento de los tokens.</p>
          </div>
          <div class="col-12 col-xl-9 security-configuration__fields security-configuration__fields--session">
            <div class="form-item">
              <label for="session-duration">Duración de sesión (inactividad)</label>
              <el-select id="session-duration" v-model="settings.sessionDuration" class="w-100">
                <el-option label="15 minutos" value="15" />
                <el-option label="30 minutos" value="30" />
                <el-option label="60 minutos" value="60" />
                <el-option label="120 minutos" value="120" />
              </el-select>
            </div>
            <div class="form-item">
              <label>Rotar token de actualización</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.rotateRefreshToken }">
                  {{ settings.rotateRefreshToken ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.rotateRefreshToken" />
              </div>
            </div>
            <div class="form-item">
              <label>Finalizar sesiones tras cambio de contraseña</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.endSessionsOnPasswordChange }">
                  {{ settings.endSessionsOnPasswordChange ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.endSessionsOnPasswordChange" />
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="security-configuration__card" shadow="never">
        <div class="row">
          <div class="col-12 col-xl-3 security-configuration__intro">
            <h2>Auditoría</h2>
            <p>Define la retención de registros de auditoría y permisos de exportación.</p>
          </div>
          <div class="col-12 col-xl-9 security-configuration__fields security-configuration__fields--audit">
            <div class="form-item">
              <label for="audit-retention">Retención de registros de auditoría</label>
              <el-select id="audit-retention" v-model="settings.auditRetention" class="w-100">
                <el-option label="90 días" value="90" />
                <el-option label="180 días" value="180" />
                <el-option label="365 días" value="365" />
                <el-option label="730 días" value="730" />
              </el-select>
            </div>
            <div class="form-item">
              <label>Permitir exportación de auditoría <span>(permiso requerido: audit.export)</span></label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.allowAuditExport }">
                  {{ settings.allowAuditExport ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.allowAuditExport" />
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="security-configuration__card" shadow="never">
        <div class="row">
          <div class="col-12 col-xl-3 security-configuration__intro">
            <h2>Aplicaciones API</h2>
            <p>Ajustes de seguridad para el acceso de aplicaciones vía API.</p>
          </div>
          <div class="col-12 col-xl-9 security-configuration__fields security-configuration__fields--api">
            <div class="form-item">
              <label>Restringir acceso por IP permitidas</label>
              <div class="security-configuration__toggle">
                <span :class="{ 'security-configuration__status--inactive': !settings.restrictAllowedIps }">
                  {{ settings.restrictAllowedIps ? "Activo" : "Inactivo" }}
                </span>
                <el-switch v-model="settings.restrictAllowedIps" />
              </div>
            </div>
            <div class="form-item security-configuration__ips">
              <label for="allowed-ips">IPs permitidas</label>
              <el-input id="allowed-ips" v-model="settings.allowedIps" class="w-100"
                placeholder="Ejemplo: 192.168.1.10, 10.0.0.5/24" />
              <small>Separa múltiples IPs con comas. Soporta rangos CIDR.</small>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <div class="security-configuration__actions">
      <button type="button" class="btn btn_red" @click="saveSettings">GUARDAR CONFIGURACIÓN</button>
      <button type="button" class="btn btn_yellow" @click="resetSettings">CANCELAR</button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.security-configuration {
  color: #30385d;
}

.security-configuration__notice {
  --el-alert-bg-color: #fffaf0;
  --el-alert-border-color: #f7d20d;
  --el-alert-text-color: #49516f;
  border: 1px solid var(--el-alert-border-color);
  border-radius: 10px;
  margin-bottom: 20px;
}

.security-configuration__notice :deep(.el-alert__icon) {
  color: #e4af00;
}

.security-configuration__sections {
  display: grid;
  gap: 16px;
}

.security-configuration__card {
  border: 1px solid #ececf5;
  border-radius: 12px;
  box-shadow: 0 5px 16px rgba(42, 47, 106, .08);
}

.security-configuration__card :deep(.el-card__body) {
  padding: 22px;
}

.security-configuration__intro h2 {
  color: #474ce6;
  font-weight: 800;
  margin: 0 0 8px;
}

.security-configuration__intro p {
  color: #7c83a2;
  margin: 0;
  max-width: 230px;
}

.security-configuration__fields {
  align-items: start;
  display: grid;
  gap: 16px 28px;
}

.security-configuration__fields--password {
  grid-template-columns: minmax(172px, 1.15fr) repeat(4, minmax(120px, 1fr));
}

.security-configuration__fields--session {
  grid-template-columns: minmax(190px, 1.2fr) repeat(2, minmax(180px, 1fr));
}

.security-configuration__fields--audit {
  grid-template-columns: minmax(220px, 1fr) minmax(280px, 1.7fr);
}

.security-configuration__fields--api {
  grid-template-columns: minmax(220px, 1fr) minmax(280px, 1.7fr);
}

.security-configuration__fields .form-item {
  margin: 0;
}

.security-configuration__fields label {
  display: block;
  margin-bottom: 7px;
}

.security-configuration__fields label span,
.security-configuration__ips small {
  color: #7c83a2;
  font-weight: 400;
}

.security-configuration__toggle {
  align-items: center;
  display: flex;
  gap: 12px;
  min-height: 42px;
}

.security-configuration__toggle > span {
  background: #effbf4;
  border: 1px solid #c9eed9;
  border-radius: 15px;
  color: #279653;
  padding: 2px 9px;
}

.security-configuration__toggle > .security-configuration__status--inactive {
  background: #f4f5f8;
  border-color: #dfe1e8;
  color: #737992;
}

.security-configuration :deep(.el-switch) {
  --el-switch-on-color: #139a4c;
  --el-switch-off-color: #b9c0d1;
}

.security-configuration :deep(.el-input-group--append) {
  border: 1px solid #000;
  border-radius: 20px;
  overflow: hidden;
}

.security-configuration :deep(.el-input-group__append) {
  background: #fff;
  border: 0 !important;
  border-radius: 0;
  box-shadow: none !important;
  color: #7c83a2;
  padding: 0 17px 0 0;
}

.security-configuration :deep(.el-input-group--append .el-input__wrapper) {
  border: 0 !important;
  border-radius: 0;
  box-shadow: none !important;
}

.security-configuration :deep(.el-input-group--append:hover),
.security-configuration :deep(.el-input-group--append:focus-within) {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.14);
}


.security-configuration__ips small {
  display: block;
  margin-top: 5px;
}

.security-configuration__actions {
  display: flex;
  gap: 20px;
  justify-content: flex-end;
  margin: 22px 0;
}

.security-configuration__actions .btn {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  justify-content: center;
  line-height: 1;
  margin: 0;
  min-height: 35px;
  padding: 0 18px;
  white-space: nowrap;
}

.security-configuration__actions .btn_red {
  color: #fff !important;
  width: 225px !important;
}

.security-configuration__actions .btn_yellow {
  color: #474ce6 !important;
  width: 166px !important;
}

@media (max-width: 1439px) {
  .security-configuration__card .row > .security-configuration__intro,
  .security-configuration__card .row > .security-configuration__fields {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .security-configuration__intro {
    margin-bottom: 18px;
  }

  .security-configuration__intro p {
    max-width: none;
  }

  .security-configuration__fields--password {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .security-configuration__fields--session,
  .security-configuration__fields--audit,
  .security-configuration__fields--api {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .security-configuration__notice {
    align-items: flex-start;
  }

  .security-configuration__card :deep(.el-card__body) {
    padding: 18px 14px;
  }

  .security-configuration__fields--password,
  .security-configuration__fields--session,
  .security-configuration__fields--audit,
  .security-configuration__fields--api {
    grid-template-columns: minmax(0, 1fr);
  }

  .security-configuration__actions {
    align-items: flex-end;
    flex-direction: column;
    gap: 10px;
  }

  .security-configuration__actions .btn {
    width: 225px !important;
  }

  .security-configuration__actions .btn_yellow {
    width: 166px !important;
  }
}
</style>
